import React, { useState, useCallback, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import { ToggleButton } from '@material-ui/lab'
import SettingsIcon from '@material-ui/icons/Settings'
import InlineCardHeader from '../../../../../molecules/Card/InlineCardHeader'
import CodeBlock from '../../../../../atoms/CodeBlock'
import OpalCard from '../../../../../opal/OpalCard'
import SydButton from '../../../../../commonDesign/Button'
import PurposeListSidebar from './configurationManagement/PurposeListSidebar'
import PurposeDetailPanel from './configurationManagement/PurposeDetailPanel'
import AddPurposeDialog from './configurationManagement/AddPurposeDialog'

function CurrentConfigurationSection ({ blueprint, isActiveVersion, onSave }) {
  const currentDefinition = blueprint.definition
  const addPurposeRef = useRef(null)
  const initialPurposes = currentDefinition?.data?.purposes || {}

  const [showJson, setShowJson] = useState(false)
  const [localPurposes, setLocalPurposes] = useState(initialPurposes)
  const [selectedPurpose, setSelectedPurpose] = useState(() => {
    const purposeCodes = Object.keys(initialPurposes)
    return purposeCodes[0] || null
  })
  const [saving, setSaving] = useState(false)

  // Track changes with useMemo (derived state)
  const isDirty = useMemo(() => {
    const original = JSON.stringify(currentDefinition?.data?.purposes || {})
    const current = JSON.stringify(localPurposes)
    return original !== current
  }, [localPurposes, currentDefinition])

  const handleAddPurpose = useCallback((parsed) => {
    setLocalPurposes(prev => ({
      ...prev,
      [parsed.purposeCode]: parsed.purposeData
    }))
    setSelectedPurpose(parsed.purposeCode)
  }, [])

  const handleRemovePurpose = useCallback((code) => {
    const purposeData = localPurposes[code]
    const confirmed = window.confirm(`Remove purpose "${purposeData?.displayName || code}"?`)
    if (confirmed) {
      setLocalPurposes(prev => {
        const updated = { ...prev }
        delete updated[code]
        return updated
      })
      if (selectedPurpose === code) {
        const remaining = Object.keys(localPurposes).filter(k => k !== code)
        setSelectedPurpose(remaining[0] || null)
      }
    }
  }, [selectedPurpose, localPurposes])

  const handleSave = useCallback(async () => {
    try {
      setSaving(true)
      await onSave({ data: { purposes: localPurposes } })
      // isDirty will automatically become false after onSave refreshes currentDefinition
    } catch (err) {
      console.error('Failed to save configuration:', err)
    } finally {
      setSaving(false)
    }
  }, [localPurposes, onSave])

  if (!currentDefinition) {
    return (
      <OpalCard size='sm'>
        <InlineCardHeader>Configuration</InlineCardHeader>
        <div style={{ padding: '16px' }}>
          <p>This blueprint has no active definition. Create a version to configure purposes.</p>
        </div>
      </OpalCard>
    )
  }

  const data = currentDefinition.data || { purposes: {} }

  return (
    <OpalCard size='sm'>
      <InlineCardHeader
        action={
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {isDirty && (
              <SydButton
                onClick={handleSave}
                processing={saving}
                variant='primary'
                size='sm'
                icon='save'
              >
                {isActiveVersion ? 'Save Changes (Active)' : 'Save Changes (Draft)'}
              </SydButton>
            )}
            <ToggleButton
              value='json'
              selected={showJson}
              onClick={() => setShowJson(p => !p)}
              size='small'
            >
              <SettingsIcon style={{ fontSize: '20px' }} />
            </ToggleButton>
          </div>
        }
      >
        Configuration
      </InlineCardHeader>

      <div style={{ padding: '16px' }}>
        {/* Active Version Warning */}
        {isActiveVersion && isDirty && (
          <div style={{
            padding: '12px',
            backgroundColor: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: '4px',
            marginBottom: '16px',
            fontSize: '0.875rem'
          }}
          >
            <strong>⚠ You are editing the active version</strong>
            <div style={{ marginTop: '4px' }}>
              Saving these changes will affect production immediately.
            </div>
          </div>
        )}

        {/* Content */}
        {showJson ? (
          <CodeBlock language='json' code={JSON.stringify(data, null, 2)} />
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: '16px', minHeight: '400px' }}>
            <PurposeListSidebar
              purposes={localPurposes}
              selectedPurpose={selectedPurpose}
              onSelectPurpose={setSelectedPurpose}
              onAddPurpose={() => addPurposeRef.current.open()}
              onRemovePurpose={handleRemovePurpose}
            />
            <PurposeDetailPanel
              purposeCode={selectedPurpose}
              purposeData={localPurposes[selectedPurpose]}
            />
          </div>
        )}
      </div>

      <AddPurposeDialog ref={addPurposeRef} onComplete={handleAddPurpose} />
    </OpalCard>
  )
}

CurrentConfigurationSection.propTypes = {
  blueprint: PropTypes.object.isRequired,
  isActiveVersion: PropTypes.bool,
  onSave: PropTypes.func.isRequired
}

CurrentConfigurationSection.defaultProps = {
  isActiveVersion: true
}

export default CurrentConfigurationSection
